import React, { RefObject } from "react";
import { n, ganttModelCode } from "./ganttTypes";
import type { GanttInputs, FullResult } from "./ganttTypes";
import { ScheduleCard } from "./ScheduleCard";
import { BufferCard } from "./BufferCard";
import { OptimiserCard } from "./OptimiserCard";
import { tNum1 } from "./ganttUnits";
import { useSettings } from "@/hooks/useSettings";

interface RightRailProps {
  inputs: GanttInputs;
  result: FullResult | null;
  optMode: "crews" | "flow";
  setOptMode: (mode: "crews" | "flow") => void;
  bufCanvasRef: RefObject<HTMLCanvasElement>;
  optCanvasRef: RefObject<HTMLCanvasElement>;
}

export function RightRail({
  inputs, result, optMode, setOptMode, bufCanvasRef, optCanvasRef,
}: RightRailProps) {
  const { uiUnits: sys } = useSettings();
  const r = result?.main;
  const otMin = Math.max(0, r?.overtime_used_h ?? 0) * 60;
  const nF = n(inputs.nFreezers, 4);
  const tonnes = r ? (r.mass_kg / 1000) : 0;
  const goalMet = tonnes >= n(inputs.goalT) && n(inputs.goalT) > 0;
  const hasGoal = n(inputs.goalT) > 0;
  const modelCode = ganttModelCode(inputs);

  // Banner: green when the target is met, blue while pending, and a neutral
  // grey "No target set" state when goal tracking is off.
  const bannerClass = !hasGoal ? "banner-neutral" : goalMet ? "banner-ok" : "banner-pend";

  // Overtime text: 1 dp hours; a sliver under 0.05 h reads in minutes, not "0.0 h".
  const otText = otMin <= 0 ? ""
    : otMin >= 3 ? `${(otMin / 60).toFixed(1)} h`
    : `${Math.max(1, Math.round(otMin))} min`;

  const summaryText = r
    ? `${nF} × ${modelCode} = ${tNum1(tonnes, sys)}${sys === "US" ? " ton" : "t"} frozen/shift (${r.batches} batches) — ${r.n_crews} crew${r.n_crews === 1 ? "" : "s"} at ${Math.round(r.crew_util * 100)}% utilisation${otText ? ` · incl. ${otText} unloading overtime` : ""}${hasGoal ? "" : " · No target set"}`
    : "—";

  return (
    <div className="railR">

      {/* Summary banner */}
      <div data-testid="gantt-summary-banner" className={bannerClass}>
        {summaryText}
      </div>

      <ScheduleCard inputs={inputs} result={result} />

      <BufferCard inputs={inputs} result={result} bufCanvasRef={bufCanvasRef} />

      <OptimiserCard
        inputs={inputs}
        result={result}
        optMode={optMode}
        setOptMode={setOptMode}
        optCanvasRef={optCanvasRef}
      />

    </div>
  );
}
